import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { RoomInfo } from '@shared/api/roomInfo/roomInfo.interface';
import { receiverNameWithHonorific } from '@shared/utils/receiverName.util';

import {
  ShopOrderForSeller,
  ShopOrderPaymentProvider,
  ShopOrderSellerRequestStatus,
} from './shopOrder.interface';

//* PG 자동환불이 안 되는 결제(가상계좌·무통장 계열) 판별 — 면세계산서/수기환불 요청 대상.
//* method는 자유 문자열('카드'·'가상계좌'·'계좌 입금'·레거시 'vbank')이라 키워드 포함으로 판별한다.
export const DEPOSIT_LIKE_METHOD_KEYWORDS = ['가상', '계좌', '입금'] as const;

//* 전화주문 입금확인 건 — PG를 안 거쳐 우리가 직접 현금영수증을 발행한다
//* (백엔드 CASH_RECEIPT_TARGET_METHOD와 같은 값)
export const CASH_RECEIPT_SELF_ISSUE_METHOD = '계좌 입금';

export const isDepositLikePayment = (paymentDetail?: { method?: string }): boolean => {
  const method = paymentDetail?.method ?? '';
  if (!method) return false;
  if (method.toLowerCase().includes('vbank')) return true;
  return DEPOSIT_LIKE_METHOD_KEYWORDS.some((keyword) => method.includes(keyword));
};

//* 토스 가상계좌 — 환불받을 계좌를 함께 보내면 PG 자동취소가 되는 유일한 입금성 결제다.
//* 부트페이 가상계좌·무통장·'계좌 입금'은 여전히 수기환불(운영사 직접 이체) 대상.
//* 판정은 문자열 휴리스틱(isDepositLikePayment)이 아니라 DB에 기록된 provider+method 정확 일치로만 한다 —
//* 어긋나면 계좌를 안 받고 취소를 시도해 토스가 거절하거나, 반대로 필요없는 계좌를 묻게 된다.
//! 목록 프로젝션(select)에 paymentDetail이 빠지면 이 판정이 조용히 false가 된다.
export const isTossVbankPayment = (paymentDetail?: {
  provider?: ShopOrderPaymentProvider;
  method?: string;
}): boolean =>
  paymentDetail?.provider === ShopOrderPaymentProvider.Tosspayments &&
  paymentDetail?.method === '가상계좌';

/* ================================================================
 * 현금영수증이 발행되는 결제인가 — 면세계산서 요청 대상 판별
 *
 * 면세계산서를 발행하려면 **먼저 현금영수증을 취소**해야 한다. 그래서 "이 주문에
 * 현금영수증이 있는가"가 계산서요청 버튼의 노출 조건이다. 그런데 `method` 문자열만으로는
 * 갈리지 않는 구간이 있다:
 *
 *  - '간편결제'(토스)는 카드로 낸 것과 머니·포인트 잔액으로 낸 것이 **같은 이름**이다.
 *    실제 판정은 승인응답의 `card` 유무 — 카드면 매출전표(현금영수증 없음), 없으면
 *    간편결제사(카카오페이·네이버파이낸셜 등)가 현금영수증을 발행한다.
 *    실측(2026-08-18, 프로덕션 4,513건): 카드 없음 2,812건 / 카드 있음 1,702건.
 *  - '카카오머니'·'페이코포인트'는 부트페이 레거시라 method 이름 자체가 머니성이다.
 *    신규 유입은 줄었지만 폴백 경로로 아직 월 15~20건 들어온다.
 *
 *! `paymentDetail.pgCard`/`pgEasyPay`는 백엔드가 아직 저장하지 않는다(스펙:
 *! docs/shop-order-easypay-cash-receipt-spec.md). 그 전까지 간편결제는 Unknown으로 떨어지며,
 *! 담당자가 영수증 링크를 열어 현금영수증/매출전표를 눈으로 확인하는 흐름을 유지한다.
 *
 * ## 우리 도장이 결제수단보다 강하다
 *
 * 우리 자진발행 범위는 계좌입금**만**이 아니다 — PG 결제 건이라도 상품교체 차액을 계좌로
 * 받았으면 그 차액은 우리가 발행한다. 그래서 결제수단만 보면 가상계좌+교체차액 건이
 * `PgIssued`로 잡히고, **PG 것만 취소한 뒤 계산서를 발행하면 우리 차액분이 살아 있는 채로
 * 매출이 두 번** 잡힌다. 카드+교체차액 건은 `CardSlip`이라 버튼 자체가 막힌다.
 *
 * 그래서 `selfCashReceiptDetail.status`를 **먼저** 본다. 우리 도장은 "우리가 발행했다"는
 * 확정 사실이라 어떤 휴리스틱보다 강하다. (docs/shop-order-cash-receipt-spec.md)
 * ================================================================ */

/** 부트페이 레거시 머니성 결제수단 — 이름만으로 현금영수증 건이 확정된다. */
const LEGACY_MONEY_METHODS = ['카카오머니', '페이코포인트'] as const;

export enum ShopOrderCashReceiptKind {
  /** '계좌 입금' — PG를 안 거쳐서 우리가 홈택스에서 자진발행한다 */
  SelfIssued = 'SelfIssued',
  /** 가상계좌·머니성 간편결제 — PG(또는 간편결제사)가 발행한다 */
  PgIssued = 'PgIssued',
  /** 카드 매출전표 — 현금영수증 자체가 없다 */
  CardSlip = 'CardSlip',
  /** 간편결제인데 PG 상세가 없어 판정 불가 (백엔드 필드 도착 전 / 구서버) */
  Unknown = 'Unknown',
}

type CashReceiptKindPayment = {
  method?: string;
  pgEasyPay?: { provider?: string; amount?: number } | null;
  pgCard?: { issuerCode?: string; cardType?: string } | null;
};

//* 주문 통째로 받는다 — 우리 도장(selfCashReceiptDetail)이 판정의 1순위라
//* paymentDetail만 넘기면 그 사실을 볼 수 없다.
type CashReceiptKindInput = {
  paymentDetail?: CashReceiptKindPayment;
  selfCashReceiptDetail?: { status?: string } | null;
};

//* 우리 도장이 살아 있는 상태 — Canceled(홈택스에서 지움)는 더 이상 현금영수증이 아니다
const SELF_ISSUE_ALIVE = ['Issued', 'CancelRequested'];

//* 우리 도장이 살아 있는가 — 이게 판정의 1순위다.
//* 계산서 처리 화면은 "취소를 실제로 했는가"를 이 값으로 확인한다 (도장이 살아 있으면 미취소).
export const hasLiveSelfCashReceipt = (order?: CashReceiptKindInput): boolean => {
  const status = order?.selfCashReceiptDetail?.status;
  return !!status && SELF_ISSUE_ALIVE.includes(status);
};

/**
 * 결제수단만으로 본 판정 — 우리 도장을 **모를 때**의 추정이다.
 * 단독으로 쓰지 말 것: 우리 자진발행분이 있어도 여기선 안 보인다.
 * (PG 발행분의 존재 여부를 따로 알아야 하는 자리에서만 쓴다 — 아래 두 발행분 공존 판정)
 */
const paymentMethodCashReceiptKind = (
  paymentDetail?: CashReceiptKindPayment,
): ShopOrderCashReceiptKind => {
  const method = paymentDetail?.method ?? '';
  if (!method) return ShopOrderCashReceiptKind.Unknown;
  if (method === CASH_RECEIPT_SELF_ISSUE_METHOD) {
    return ShopOrderCashReceiptKind.SelfIssued;
  }
  if (LEGACY_MONEY_METHODS.some((m) => method === m)) {
    return ShopOrderCashReceiptKind.PgIssued;
  }
  //* 가상계좌·무통장은 토스/부트페이가 입금 확인 시 발행한다 (계좌 입금은 위에서 걸러짐)
  if (isDepositLikePayment(paymentDetail)) return ShopOrderCashReceiptKind.PgIssued;
  if (method.includes('간편')) {
    //* card가 실려 있으면 카드로 낸 것 — 잔액 결제였다면 이 자리가 비어 있다.
    if (paymentDetail?.pgCard) return ShopOrderCashReceiptKind.CardSlip;
    //* easyPay만 있고 card가 없다 = 머니·포인트 잔액 결제 → 현금영수증 발행 건
    if (paymentDetail?.pgEasyPay) return ShopOrderCashReceiptKind.PgIssued;
    return ShopOrderCashReceiptKind.Unknown;
  }
  return ShopOrderCashReceiptKind.CardSlip;
};

export const shopOrderCashReceiptKind = (
  order?: CashReceiptKindInput,
): ShopOrderCashReceiptKind =>
  //*! 결제수단보다 우리 도장을 먼저 본다 — PG 결제 건의 교체 차액도 우리 발행분이라,
  //*! 결제수단만 보면 PgIssued/CardSlip으로 잡혀 우리 발행분이 취소에서 누락된다
  hasLiveSelfCashReceipt(order)
    ? ShopOrderCashReceiptKind.SelfIssued
    : paymentMethodCashReceiptKind(order?.paymentDetail);

/**
 * 우리 자진발행분의 발행액 — 홈택스에 친(또는 칠) 금액.
 * 계좌입금 주문은 결제 전액, PG 결제 건은 **교체 차액만**이다(원결제분은 PG가 발행).
 * (docs/shop-order-cash-receipt-spec.md ②)
 */
export const selfCashReceiptAmount = (order?: {
  paymentDetail?: { method?: string; paymentByCash?: number };
  orderDetail?: { itemChanges?: { additionalPaymentByCash?: number }[] };
}): number => {
  if (order?.paymentDetail?.method === CASH_RECEIPT_SELF_ISSUE_METHOD) {
    return order.paymentDetail.paymentByCash ?? 0;
  }
  return (order?.orderDetail?.itemChanges ?? []).reduce(
    (sum, change) => sum + (change.additionalPaymentByCash ?? 0),
    0,
  );
};

/**
 * PG 발행분과 우리 자진발행분이 **둘 다** 있는 주문인가.
 *
 * 가상계좌·머니성 간편결제 주문에 상품교체 차액이 붙으면 한 주문에 현금영수증이 둘이고
 * **취소도 각각** 해야 한다. 한쪽만 취소하고 계산서를 발행하면 매출이 두 번 잡힌다.
 * (실측 2건 — docs/shop-order-cash-receipt-spec.md)
 */
export const hasBothCashReceipts = (order?: CashReceiptKindInput): boolean =>
  hasLiveSelfCashReceipt(order) &&
  paymentMethodCashReceiptKind(order?.paymentDetail) ===
    ShopOrderCashReceiptKind.PgIssued;

/**
 * 면세계산서 요청(=현금영수증 취소 후 발행) 대상인가 — **현금영수증이 확인된 건만** true.
 *
 *! 판정 불가(Unknown)는 **닫는다.** 간편결제의 38%(1,702/4,513)는 카드 건이라,
 *! 모르는 걸 열어두면 판매자에겐 "가능"으로 보이고 관리자 반려로 끝난다. 못 하는 걸
 *! 되는 것처럼 보여주는 쪽이, 되는 걸 잠깐 못 하는 쪽보다 나쁘다.
 *! Unknown 구간은 백엔드가 `paymentDetail.pgCard`를 채우는 순간 저절로 사라진다
 *! (docs/shop-order-easypay-cash-receipt-spec.md — 백엔드 구현 완료·배포/백필 대기).
 */
export const canRequestShopOrderTaxInvoice = (order?: CashReceiptKindInput): boolean => {
  const kind = shopOrderCashReceiptKind(order);
  return (
    kind === ShopOrderCashReceiptKind.SelfIssued ||
    kind === ShopOrderCashReceiptKind.PgIssued
  );
};

/**
 * 카드 목록의 좁은 자리용 결제수단 축약.
 * 접미 `#` = 현금영수증이 발행된 건 ('가상#'·'입금#'·'간편#'). 카드 건은 아무것도 안 붙는다.
 */
export const shopOrderMethodShortLabel = (order?: CashReceiptKindInput): string => {
  const method = order?.paymentDetail?.method ?? '';
  if (!method) return '';
  if (method === '가상계좌') return '가상#';
  if (method === CASH_RECEIPT_SELF_ISSUE_METHOD) return '입금#';
  //* 현금영수증 건은 '가상#'·'입금#'과 같은 규칙으로 #만 붙인다 — 카드 건은 무표시가 기본값
  if (method.includes('간편')) {
    return shopOrderCashReceiptKind(order) === ShopOrderCashReceiptKind.PgIssued
      ? '간편#'
      : '간편';
  }
  return method.slice(0, 2);
};

/**
 * 취소 API 실패 문구. 서버는 거절 사유를 담당자가 읽을 수 있는 한국어 `message`로 내려준다
 * (계좌 누락·은행 미인식·이중환불 차단·토스 거절 등).
 *! `error` 코드로 분기하면 안 된다 — 전역 HttpExceptionFilter가 `error`를 예외 클래스명으로
 *! 덮어써서 `MANUAL_REFUND_IN_PROGRESS` 같은 코드는 프론트에 도달하지 않는다. 400의 message만 쓴다.
 *! 그 외 상태코드의 message는 내부 문구(_assert 영문·axios 원문)라 사용자에게 쓸 수 없다.
 */
export const shopOrderCancelErrorMessage = (
  err: unknown,
  fallback = '취소에 실패했습니다.',
): string => {
  const response = (
    err as { response?: { status?: number; data?: { message?: string } } }
  )?.response;
  if (response?.status === 400 && response.data?.message) return response.data.message;
  switch (response?.status) {
    case 403:
      return '권한이 없습니다.';
    case 404:
      return '주문을 찾을 수 없습니다.';
    default:
      return fallback;
  }
};

export const sellerRequestStatusLabel: Record<ShopOrderSellerRequestStatus, string> = {
  Requested: '대기',
  Done: '완료',
  Rejected: '반려',
};

//* Bugo.fevent 타입(Fevent 제네릭 기본값)은 funeralHomeInfo·roomInfo ref를 string으로 두지만,
//* 셀러 목록·과거배송검색 쿼리가 populate하므로 런타임엔 객체가 온다. 미populate면 undefined.
const asPopulated = function <T>(value: T | string | undefined): T | undefined {
  return typeof value === 'object' && value !== null ? value : undefined;
};

//* 부고(fevent) 기준 현재 상가 주소 — 주문 생성 시 receiverAddress 조합식과 동일
//* (ShopItemCheckoutPage 참조). 호실이동으로 fevent가 갱신됐다면 새 호실이 나온다.
//* bugo 없음(전화주문 등)·주소 정보 미populate면 undefined.
export const feventCurrentAddress = (
  shopOrder: ShopOrderForSeller,
): string | undefined => {
  const fevent = shopOrder.bugo?.fevent;
  if (!fevent) return undefined;
  const funeralHome = asPopulated<FuneralHomeInfo>(fevent.funeralHomeInfo);
  const room = asPopulated<RoomInfo>(fevent.roomInfo);
  const address = fevent.funeralHomeInfoEmbed?.address ?? funeralHome?.address;
  if (!address) return undefined;
  const homeName = funeralHome?.name ?? fevent.funeralHomeInfoEmbed?.name ?? '';
  const roomName = fevent.roomInfoEmbed?.name ?? room?.name ?? '';
  return `${address}, [${homeName}, ${roomName}]`;
};

//* 쉼표·공백 등 표기 차이만 다른 주소를 같다고 보기 위한 정규화
//* (구 조합식은 괄호 앞 ', ' 구분자가 없어 "…(독곡동)[제일장례식장, 202호실]" 형태로 저장됨)
const normalizeAddress = (address: string): string => address.replace(/[\s,]/g, '');

//* 부고 기준 현재 상가 주소와 주문에 저장된 배송지 주소의 불일치 여부 (호실이동 감지)
export const feventAddressMismatch = (shopOrder: ShopOrderForSeller): boolean => {
  const feventAddress = feventCurrentAddress(shopOrder);
  return (
    feventAddress !== undefined &&
    normalizeAddress(feventAddress) !==
      normalizeAddress(shopOrder.deliveryDetail.receiverAddress)
  );
};

export const orderContentString = (shopOrder: ShopOrderForSeller) => {
  return (
    `${shopOrder.orderDetail.shopItemEmbed.name}\n\n` +
    `주소: ${shopOrder.deliveryDetail.receiverAddress}\n` +
    `문구(보내는분): ${shopOrder.bugoDetail.senderPhrase}\n` +
    `조의문구: ${shopOrder.bugoDetail.condolencePhrase}\n` +
    //* receiverName은 이미 "OOO 상주님"으로 저장돼 있다 — 덧붙이면 "상주님 상주님"이 된다
    `받으시는분: ${receiverNameWithHonorific(shopOrder.deliveryDetail.receiverName)}\n` +
    `도매가: ${shopOrder.orderDetail.shopItemEmbed.priceWhole.toLocaleString()}원`
  );
};
